import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslationApp } from "../../lib/i18next";
import {
  ADS_CAMPAIGN_LOCATION_EXAMPLE,
  ADS_CAMPAIGN_LOCATION_PLACEHOLDER,
  FIELD_REQUIRED,
  SEARCH_SPECIFIC_LOCATION_EXAMPLE,
  SEARCH_SPECIFIC_LOCATION_PLACEHOLDER,
  TYPE_LEAST_CHARACTERS,
} from "../../i18n/keysTranslations";
import useMapsFeatures from "../../hooks/useMapsFeatures";
import { startGetLocationsByPrompt } from "../../actions/adsCampaigns";
import { useDispatchApp } from "../../lib/redux";
import { useMap } from "@vis.gl/react-google-maps";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import { clampRadiusMeters } from "./utils/radiusLimits";

// Utility function to detect and parse coordinates
const detectCoordinates = (input) => {
  if (!input || typeof input !== "string") return null;

  // Remove extra spaces and trim
  const cleanInput = input.trim().replace(/\s+/g, " ");

  // Pattern for decimal degrees: "40.7128, -74.0060" or "40.7128,-74.0060"
  const decimalPattern = /^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/;

  // Pattern for degrees with symbols: "40.7128° N, 74.0060° W" or similar
  const degreePattern =
    /^(-?\d+\.?\d*)[°]?\s*([NS])?,?\s*(-?\d+\.?\d*)[°]?\s*([EW])?$/i;

  let match = cleanInput.match(decimalPattern);
  if (match) {
    const lat = parseFloat(match[1]);
    const lng = parseFloat(match[2]);

    // Validate coordinate ranges
    if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      return { lat, lng };
    }
  }

  // Try degree pattern
  match = cleanInput.match(degreePattern);
  if (match) {
    let lat = parseFloat(match[1]);
    let lng = parseFloat(match[3]);

    // Handle N/S for latitude
    if (match[2] && match[2].toUpperCase() === "S") {
      lat = -Math.abs(lat);
    }

    // Handle E/W for longitude
    if (match[4] && match[4].toUpperCase() === "W") {
      lng = -Math.abs(lng);
    }

    // Validate coordinate ranges
    if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      return { lat, lng };
    }
  }

  return null;
};

const SearchLocationMaps = ({
  onSelectLocation,
  onAddContext,
  onAddMarker,
  disableAISearch,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [predictionResults, setPredictionResults] = useState([]);

  const {
    control,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      prompt: "",
    },
    mode: "onChange",
  });

  const { t } = useTranslationApp();
  const {
    getPredictionsByKeyword,
    geocoder,
    parseLocationNames,
    getLocationNameByLatLng,
    onSearchByCoordinates,
    getLocationTextByLocationObject,
  } = useMapsFeatures();
  const dispatch = useDispatchApp();
  const map = useMap();

  const onInputChange = async (_, newValue) => {
    setValue("prompt", newValue);
    const predictions = await getPredictionsByKeyword(newValue);
    setPredictionResults(predictions);
  };

  const onSendPosition = async (_, position) => {
    const response = await onSelectLocation(position);
    if (!response) return;
    setValue("prompt", "");
  };

  const geocodeCitiesAndAddMarkers = async (locations) => {
    if (!geocoder) return;

    let lastAssignedFocus = "";
    let firstAssignedFocus = "";

    console.log("locations", locations);

    for (const location of locations) {
      try {
        if (location.type === "radius") {
          const responseName = await getLocationNameByLatLng({
            lat: location.lat,
            lng: location.lng,
          });

          onAddMarker({
            lat: location.lat,
            lng: location.lng,
            type: "radius",
            radius: clampRadiusMeters(location.radius),
            name: location.zone,
            longName: `${location.zone}, ${location.city}, ${location.country}`,
            id: responseName.id,
            metadata: responseName.metadata || {
              place_id: responseName.id,
              address_components: [],
              types: [],
              formatted_address: "",
              geometry: {
                location: { lat: location.lat, lng: location.lng },
                viewport: null,
              },
            },
          });

          if (responseName?.metadata?.geometry?.viewport) {
            if (!firstAssignedFocus) {
              firstAssignedFocus = true;
              map.fitBounds(responseName.metadata.geometry.viewport);
            }
            lastAssignedFocus = responseName.metadata.geometry.viewport;
          }

          continue;
        }

        const locationText = getLocationTextByLocationObject(location);
        const response = await geocoder.geocode({ address: locationText });
        const results = response.results || [];
        console.log("results", results);

        if (results[0]) {
          const result = results[0];
          const responseNames = parseLocationNames(result, result.types);
          const { name, longName } = responseNames;
          const lat = result.geometry.location.lat();
          const lng = result.geometry.location.lng();
          onAddMarker({
            lat,
            lng,
            name:
              name ||
              result?.address_components[0]?.long_name ||
              result?.address_components[0]?.short_name ||
              "",
            longName: longName || result?.formatted_address || "",
            id: result.place_id,
            metadata: {
              place_id: result.place_id,
              address_components: result.address_components || [],
              types: result.types || [],
              formatted_address: result.formatted_address || "",
              geometry: {
                location: { lat, lng },
                viewport: result.geometry?.viewport
                  ? {
                      south: result.geometry.viewport.getSouthWest().lat(),
                      west: result.geometry.viewport.getSouthWest().lng(),
                      north: result.geometry.viewport.getNorthEast().lat(),
                      east: result.geometry.viewport.getNorthEast().lng(),
                    }
                  : null,
              },
            },
          });
          if (result.geometry.viewport) {
            if (!firstAssignedFocus) {
              firstAssignedFocus = true;
              map.fitBounds(result.geometry.viewport);
            }
            lastAssignedFocus = result.geometry.viewport;
          }
        }
      } catch (error) {
        console.error("Error geocoding city:", location, error);
      }
    }
    if (lastAssignedFocus) {
      map.fitBounds(lastAssignedFocus);
    }
  };

  const execSearchByCoordinates = async (coordinates) => {
    const response = await onSearchByCoordinates({
      coordinates,
      onCallbackLoading: (isLoading) => {
        setIsLoading(isLoading);
      },
    });
    if (response.ok) {
      const { data } = response;
      onAddMarker(data);
      if (map) {
        map.setCenter(coordinates);
        map.setZoom(15);
      }
      setValue("prompt", "");
      setPredictionResults([]);
    } else {
      const predictions = await getPredictionsByKeyword(
        `${coordinates.lat}, ${coordinates.lng}`
      );
      setPredictionResults(predictions);
    }
  };

  const onFindLocationsByPrompt = async (prompt) => {
    if (!prompt) return;
    setIsLoading(true);
    const response = await dispatch(startGetLocationsByPrompt(prompt));
    if (response.ok) {
      const locations = response.data.locations;
      if (locations.length > 0) {
        onAddContext(prompt);
        await geocodeCitiesAndAddMarkers(locations);
      }
    }
    setIsLoading(false);
  };

  return (
    <Box>
      <Controller
        name="prompt"
        control={control}
        rules={{
          required: {
            value: true,
            message: t(FIELD_REQUIRED),
          },
          minLength: {
            value: 3,
            message: t(TYPE_LEAST_CHARACTERS, { value: 3 }),
          },
        }}
        render={({ field }) => (
          <>
            <Autocomplete
              loading={isLoading}
              hasLabel={true}
              fullWidth
              freeSolo
              disableClearable
              selectOnFocus
              clearOnEscape
              clearOnBlur
              blurOnSelect
              options={predictionResults}
              getOptionLabel={(option) => option.description || ""}
              inputValue={field.value}
              onInputChange={onInputChange}
              value={null}
              onChange={(e, value, reason) => {
                if (reason === "createOption") {
                  // First, check if the input looks like coordinates
                  const coordinates = detectCoordinates(field.value);

                  if (coordinates) {
                    // If coordinates detected, search by lat/lng directly
                    execSearchByCoordinates(coordinates);
                    return;
                  }

                  // If disableAISearch is true, don't search by prompt
                  if (!disableAISearch) {
                    onFindLocationsByPrompt(field.value);
                  }
                }
                if (reason === "selectOption") onSendPosition(e, value);
              }}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                const mainText = option.structured_formatting.main_text;
                const secondaryText =
                  option.structured_formatting.secondary_text;
                return (
                  <Box
                    key={key}
                    {...optionProps}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      justifyContent: "flex-start",
                    }}
                  >
                    <Typography
                      variant="body2"
                      textAlign="left"
                      sx={{
                        width: "100%",
                      }}
                    >
                      {mainText}
                    </Typography>
                    <Typography
                      variant="caption"
                      textAlign="left"
                      sx={{
                        width: "100%",
                      }}
                    >
                      {secondaryText}
                    </Typography>
                  </Box>
                );
              }}
              componentsProps={{
                popper: {
                  sx: {
                    zIndex: 9999,
                  },
                },
              }}
              isOptionEqualToValue={(option, value) => {
                if (!option || !value) return true;
                return option.place_id === value.place_id;
              }}
              sx={{
                "& .MuiFormControl-root ": {
                  pl: 0.75,
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  // size="small"
                  error={Boolean(errors.prompt)}
                  label={t(
                    disableAISearch
                      ? SEARCH_SPECIFIC_LOCATION_PLACEHOLDER
                      : ADS_CAMPAIGN_LOCATION_PLACEHOLDER
                  )}
                  placeholder={t(
                    disableAISearch
                      ? SEARCH_SPECIFIC_LOCATION_EXAMPLE
                      : ADS_CAMPAIGN_LOCATION_EXAMPLE
                  )}
                  variant="filled"
                  InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {isLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                  sx={{
                    border: `1px solid ${INPUT_COLOR_BORDER}`,
                    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                    backgroundColor: "fields.background",
                    "& .MuiInputLabel-root": {
                      mt: 1,
                      ml: 0.75,
                    },
                  }}
                />
              )}
            />
            {errors.prompt && (
              <Typography variant="caption" color="error">
                {errors.prompt.message}
              </Typography>
            )}
          </>
        )}
      />
    </Box>
  );
};

export default SearchLocationMaps;
